import { useCartContext } from "app/providers/CartProvider";
import classNames from "classnames";
import ProductItemConfigTable from "entities/product-item-config-table";
import ProductItemPrice from "entities/product-item-price";
import { zakelijkDoorCodes } from "features/option-controls/door-width-controls/ZakelijkHelper";
import { useMemo } from "react";
import { OrderProduct, Product } from "shared/api/middleware-api-client";
import OptionConfigurationImage from "shared/ui/option-configuration-image";
import Heading from "shared/ui/typography/Heading";
import { concatClassnames } from "shared/utils/concatClassnames";

interface ProductItemCheckoutProps {
  product: Product | OrderProduct;
  controls: JSX.Element;
  reorderControl?: boolean;
}

const ProductItemCheckout = ({
  product,
  controls,
  reorderControl = false,
}: ProductItemCheckoutProps) => {
  const { isSampleSale, getCart } = useCartContext();
  const cart = getCart();

  const imageId = useMemo(() => {
    if ("doorLayout" in product) {
      return product.doorLayout.imageId;
    }

    return product.imageId;
  }, [product]);

  const doorName = () => {
    if (isSampleSale) {
      return cart.sampleSaleReferenceNumber;
    }

    if ("doorType" in product) {
      return product.doorType.name;
    }

    return product.doorOption;
  };

  return (
    <>
      <div
        className={concatClassnames(
          "gg-px-4 sm:gg-px-14 md:gg-px-32 lg:gg-px-0",
          "gg-col-span-12 lg:gg-col-span-8 xl:gg-col-span-7",
          "gg-flex",
          "gg-mb-6 md:gg-mb-10 lg:gg-mb-0",
        )}
      >
        <div
          className={concatClassnames(
            "gg-h-24 gg-w-24 gg-shrink-0 sm:gg-h-32 sm:gg-w-32 lg:gg-h-40 lg:gg-w-40",
            "gg-mr-7",
          )}
        >
          <OptionConfigurationImage
            imageId={imageId}
            className="gg-mt-0.5 gg-h-24 gg-w-24 gg-shrink-0 gg-bg-gray-100 gg-object-cover gg-object-center sm:gg-h-32 sm:gg-w-32 lg:gg-h-40 lg:gg-w-40"
          />
        </div>
        <div className={concatClassnames("gg-grid gg-w-full")}>
          <Heading headingLevel="h2" className="">
            {doorName()}
          </Heading>

          <ProductItemConfigTable product={product} />
        </div>
      </div>

      <div
        className={concatClassnames(
          "gg-order-2 gg-col-span-12 lg:gg-order-1 lg:gg-col-span-3",
          "gg-border-t gg-py-3.5 lg:gg-border-0 lg:gg-py-0",
          reorderControl ? "gg-self-start lg:gg-py-2.5" : "gg-self-center",
        )}
      >
        <div className="gg-px-4 lg:gg-px-0">{controls}</div>
      </div>

      <ProductItemPrice
        className={classNames(
          "gg-mb-6 gg-ml-[2px] gg-w-28 sm:gg-w-32 md:gg-mb-10 lg:gg-mb-0 lg:gg-mt-[1.35rem] lg:gg-w-28",

          {
            "gg-order-1 gg-col-span-5 gg-col-start-8 sm:gg-col-start-6 md:gg-col-start-7 lg:gg-order-2 lg:gg-col-span-1 xl:gg-col-span-1 xl:gg-col-start-12":
              !isSampleSale,
            "gg-order-1 gg-col-span-5 gg-col-start-10 sm:gg-col-start-10 md:gg-col-start-9 md:gg-pl-8 lg:gg-order-2 lg:gg-col-span-1 lg:gg-pl-0 xl:gg-col-span-1 xl:gg-col-start-12":
              isSampleSale,
          },
        )}
        product={product}
      />
    </>
  );
};
export default ProductItemCheckout;
